import * as Sentry from '@sentry/vue';
import { uniqBy } from 'lodash-es';

import { isTheme } from '../../../utils/production';
import { collectTimelineValues } from '../../../utils/timeline-collectors';

import { getThemes, getMultipleThemes, getTags } from '../../../services/themes';

const state = {
  themes: [],
  socialDisplayTags: [],
  lookupTable: {},

  // Track which themes are being loaded by ID
  isLoadingMap: {},

  // Track which themes are confirmed missing (deleted) by ID
  notFoundMap: {},

  isLoading: false,
  page: 1,
  limit: 10,
  total: 0,
  tags: [],
  search: '',
};

const actions = {
  /**
   * Get list of themes, paginated.
   */
  async getThemes(context) {
    const { tags, page, search } = context.state;

    context.commit('SET_THEMES_FIELD', { key: 'isLoading', value: true });

    // Paginate the themes (for producer)
    const opts = { page, search };

    if (tags.length) {
      opts.tags = tags.join(',');
    }

    const { themes, total } = await getThemes(opts).catch((err) => {
      Sentry.captureException(err);
      console.error('Failed to fetch themes:', err);

      return {
        themes: [],
        total: 0,
      };
    });

    context.commit('SET_THEMES_FIELD', { key: 'isLoading', value: false });
    context.commit('SET_THEMES', themes);
    context.commit('ADD_THEMES_TO_LOOKUP_TABLE', themes);
    context.commit('SET_THEMES_FIELD', { key: 'total', value: total });
  },

  async getSocialDisplayTags({ commit }) {
    const { tags } = await getTags();
    const sortedTags = [...tags].sort((a, b) => a.localeCompare(b));

    commit('SET_THEMES_FIELD', { key: 'socialDisplayTags', value: sortedTags });
  },

  /**
   * Load themes currently in the production playlist.
   * This can be ran over and over and will only load what's missing in the lookup table.
   */
  async getTimelineThemes(context) {
    const { timeline } = context.rootState.production.production;

    let themeIds = collectTimelineValues(timeline, (item) => {
      const theme = context.state.lookupTable[item._id];

      // Ignore non-themes, themes that already loaded (and have `settings`),
      // and themes already confirmed missing.
      if (isTheme(item.layout) && !theme?.settings && !context.state.notFoundMap[item._id] && item._id) {
        return item._id;
      }

      return null;
    }, new Set());

    themeIds = [...themeIds];

    if (!themeIds.length) {
      return;
    }

    context.commit('SET_THEMES_LOADING', { ids: themeIds, loading: true });

    try {
      const { themes } = await getMultipleThemes(themeIds);

      context.commit('ADD_THEMES_TO_LOOKUP_TABLE', themes);

      // Any theme requested but missing from the response is presumed deleted.
      // We only do this on a successful response, so network errors don't
      // incorrectly mark resources as missing.
      const returnedIds = new Set(themes.map((theme) => theme._id));
      const notFoundIds = themeIds.filter((id) => !returnedIds.has(id));
      if (notFoundIds.length) {
        context.commit('SET_THEMES_NOT_FOUND', { ids: notFoundIds, notFound: true });
      }
    } catch (err) {
      Sentry.captureException(err);
      console.error('Failed to fetch multiple themes', err);
    } finally {
      context.commit('SET_THEMES_LOADING', { ids: themeIds, loading: false });
    }
  },

  /**
   * Increment page number and fetch some more themes
   */
  incrementThemesPage(context) {
    context.commit('SET_THEMES_FIELD', { key: 'page', value: context.state.page + 1 });
    context.dispatch('getThemes');
  },

  setThemes(context, payload) {
    context.commit('SET_THEMES', payload);
  },

  /**
   * Clear current themes array and reset pagination
   */
  resetThemes(context) {
    context.commit('RESET_THEMES');
  },

  changeSocialDisplayTags({ commit, dispatch }, tags) {
   commit('SET_THEMES_FIELD', { key: 'tags', value: tags });
   dispatch('resetThemes');
   dispatch('getThemes');
  },

  changeSocialDisplaySearch({ commit, dispatch }, search) {
   commit('SET_THEMES_FIELD', { key: 'search', value: search });
   dispatch('resetThemes');
   dispatch('getThemes');
  },

  /**
   * Clear any tags used to filter results, reset page to 1, then refetch themes
   */
  resetSocialDisplaySearch(context) {
    context.commit('CLEAR_THEME_TAGS');
    context.commit('SET_THEMES_FIELD', { key: 'search', value: '' });
    context.dispatch('resetThemes');
    context.dispatch('getThemes');
  },
};

const mutations = {
  /* eslint-disable no-param-reassign, no-shadow */

  SET_THEMES_FIELD(state, { key, value }) {
    state[key] = value;
  },

  /**
   * Set loading flags for specific themes by ID,
   * so we know in the theme editor if a theme is still loading.
   */
  SET_THEMES_LOADING(state, { ids, loading }) {
    state.isLoadingMap = {
      ...state.isLoadingMap,
      ...ids.reduce((memo, id) => ({
        ...memo,
        [id]: loading,
      }), {}),
    };
  },

  SET_THEMES_NOT_FOUND(state, { ids, notFound }) {
    state.notFoundMap = {
      ...state.notFoundMap,
      ...ids.reduce((memo, id) => ({
        ...memo,
        [id]: notFound,
      }), {}),
    };
  },

  SET_THEMES(state, themes) {
    state.themes = uniqBy(state.themes.concat(themes), '_id');
  },

  ADD_THEME_TAG(state, tag) {
    state.tags = [...state.tags, tag];
  },

  REMOVE_THEME_TAG(state, tag) {
    state.tags = state.tags.filter((t) => t !== tag);
  },

  CLEAR_THEME_TAGS(state) {
    state.tags = [];
  },

  RESET_THEMES(state) {
    state.themes = [];
    state.page = 1;
    state.total = 0;
  },

  ADD_THEMES_TO_LOOKUP_TABLE(state, themes) {
    state.lookupTable = {
      ...state.lookupTable,
      ...(themes?.reduce((memo, theme) => ({
        ...memo,
        [theme._id]: {
          ...state.lookupTable[theme._id],
          ...theme,
        },
      }), {})),
    };
  },
};

const getters = {
  hasMoreThemes(state) {
    return state.total > (state.page * state.limit);
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
